import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { cn, getContrastTextColor } from '../lib/utils';
import NotificationBell from './NotificationBell';
import PWAInstallPrompt from './PWAInstallPrompt';
import {
  LayoutDashboard,
  Briefcase,
  Calendar,
  ClipboardList,
  Clock,
  FileText,
  Users,
  Contact,
  Settings,
  Shield,
  HardDrive,
  ShieldAlert,
  Trash2,
  User,
  LogOut,
  Menu,
  X,
  BarChart3,
  Wrench,
  Target,
  FileCheck,
  Archive,
  Receipt,
  Building2,
  Repeat,
  Radio,
  Timer,
  Search,
  BookOpen,
  Download,
  MessageSquarePlus,
  Bell,
  Award,
  CheckCircle2,
  PackageOpen,
  Camera,
  History,
  MapPin
} from 'lucide-react';
import { Button } from './ui/button';
import GlobalSearch from './GlobalSearch';

// Navigation ordered to match business flow:
// Contacts → Companies → Opportunities → Proposals → Jobs → Calendar → Timesheet → Reports
const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/', viewKey: 'dashboard' },
  { icon: Bell, label: 'Reminders', path: '/reminders', viewKey: 'reminders' },
  { icon: Award, label: 'My Certs', path: '/my-certifications', viewKey: 'my_certs' },
  { icon: Contact, label: 'Contacts', path: '/people', viewKey: 'contacts' },
  { icon: Building2, label: 'Companies', path: '/companies', viewKey: 'companies' },
  { icon: Target, label: 'Opportunities', path: '/opportunities', permission: 'view_proposals', viewKey: 'opportunities' },
  { icon: FileCheck, label: 'Proposals', path: '/proposals', permission: 'view_proposals', viewKey: 'proposals' },
  { icon: Briefcase, label: 'Jobs', path: '/jobs', viewKey: 'jobs' },
  { icon: Calendar, label: 'Calendar', path: '/calendar', viewKey: 'calendar' },
  { icon: Clock, label: 'Timesheet', path: '/timesheet', viewKey: 'timesheet' },
  { icon: CheckCircle2, label: 'Approve Time', path: '/timesheet/approval', adminOnly: true, viewKey: 'timesheet_approval' },
  { icon: BarChart3, label: 'Hours by Job', path: '/timesheet/hours-by-job', adminOnly: true, viewKey: 'hours_by_job' },
  { icon: Timer, label: 'Time Tracker', path: '/field-tracker-v2', viewKey: 'time_tracker' },
  { icon: FileText, label: 'Field Reports', path: '/field-reports', viewKey: 'field_reports' },
];

const moreItems = [
  { icon: ClipboardList, label: 'My Jobs', path: '/my-jobs', viewKey: 'my_jobs' },
  { icon: Repeat, label: 'Recurring Jobs', path: '/recurring-jobs', viewKey: 'recurring_jobs' },
  { icon: Radio, label: '811 Info', path: '/811-info', viewKey: '811_info' },
  { icon: BarChart3, label: 'Reports', path: '/reports', permission: 'view_reports', viewKey: 'reports' },
  { icon: Receipt, label: 'Expenses', path: '/expense-reports', viewKey: 'expenses' },
  { icon: Briefcase, label: 'Office', path: '/office', permission: 'office_access', viewKey: 'office' },
  { icon: Users, label: 'Team', path: '/team', permission: 'manage_team', viewKey: 'team' },
  { icon: Award, label: 'Certifications', path: '/certifications', permission: 'manage_certifications', viewKey: 'certifications' },
  { icon: Wrench, label: 'Maintenance', path: '/maintenance', viewKey: 'maintenance' },
  { icon: MapPin, label: 'Equipment Overview', path: '/equipment-overview', viewKey: 'equipment_overview' },
  { icon: Archive, label: 'Archive', path: '/archive', viewKey: 'archive' },
  { icon: BookOpen, label: 'User Manual', path: '/api/export/user-manual-html', external: true, viewKey: 'user_manual' },
];

const adminItems = [
  { sectionHeader: 'Administration',
    icon: Settings, label: 'Services', path: '/services', permission: 'manage_services' },
  { icon: Shield, label: 'Permissions', path: '/permissions', adminOnly: true },
  { icon: HardDrive, label: 'Storage Health', path: '/admin/storage', adminOnly: true },
  // Consolidated admin utilities — single nav entry, all the tools live on
  // a hub page so the left rail stays focused on day-to-day work. Future
  // admin pages should be added to AdminToolsPage.jsx, not back to this
  // sidebar.
  { icon: Wrench, label: 'Admin Tools', path: '/admin/tools', adminOnly: true },
];

// (The old per-tool entries — Job Data Audit, Deleted Jobs, Equipment
// Recovery, Photo Audit, Photo History, Signature Purge — now live on
// the /admin/tools hub page.)
const adminToolsItems = [];

// Mobile/PWA bottom-nav lineup. Picked specifically for what the field crew
// actually taps every shift: Dashboard, Time Tracker, Calendar (replaces
// My Certs — barely touched on mobile), Jobs, Reminders.
const bottomNavItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/', viewKey: 'dashboard' },
  { icon: Timer, label: 'Time Tracker', path: '/field-tracker-v2', viewKey: 'time_tracker' },
  { icon: Calendar, label: 'Calendar', path: '/calendar', viewKey: 'calendar' },
  { icon: Briefcase, label: 'Jobs', path: '/jobs', viewKey: 'jobs' },
  { icon: Bell, label: 'Reminders', path: '/reminders', viewKey: 'reminders' },
];

export default function Layout({ children }) {
  const { user, logout, isAdmin, isSuperUser, hasPermission, isImpersonating, stopImpersonating } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  // Two small placeholder slots the single NotificationBell instance
  // below portals its button into — one in the mobile header, one in the
  // desktop floating puck — so there's only one mount/one 60s poll while
  // each bell still appears exactly where it did before.
  const [mobileBellSlot, setMobileBellSlot] = useState(null);
  const [desktopBellSlot, setDesktopBellSlot] = useState(null);
  
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Filter nav items based on permissions + per-user page visibility
  const filterByPermission = (items) => {
    return items.filter(item => {
      // Admin-only items (like Permissions) - require isAdmin or isSuperUser
      if (item.adminOnly) {
        return isAdmin() || isSuperUser?.();
      }
      // Items with permission requirement - check the toggle
      if (item.permission) {
        if (!(hasPermission(item.permission) || isAdmin() || isSuperUser?.())) {
          return false;
        }
      }
      // Per-user page visibility (non-admins only — admins always see)
      if (item.viewKey && !(isAdmin() || isSuperUser?.())) {
        const pv = user?.page_visibility || {};
        if (pv[item.viewKey] === false) return false;
      }
      return true;
    });
  };

  const filteredNavItems = filterByPermission(navItems);
  const filteredMoreItems = filterByPermission(moreItems);
  const filteredAdminItems = filterByPermission(adminItems);
  const filteredAdminToolsItems = filterByPermission(adminToolsItems);
  const allNavItems = [...filteredNavItems, ...filteredMoreItems, ...filteredAdminItems, ...filteredAdminToolsItems];
  // Respect each user's page_visibility map so a hidden Jobs/Calendar
  // doesn't reappear via the bottom nav.
  const filteredBottomNavItems = filterByPermission(bottomNavItems);

  return (
    <div className="min-h-screen bg-white">
      {/* Impersonation banner */}
      {isImpersonating && (
        <div
          className="fixed top-0 left-0 right-0 z-[60] bg-amber-500 text-slate-900 text-sm font-medium px-3 py-2 flex flex-wrap items-center justify-center gap-2 shadow-md"
          style={{
            // Respect iOS notch / Dynamic Island so the STOP button isn't hidden
            // behind the camera area in PWA / standalone mode.
            paddingTop: 'calc(0.5rem + env(safe-area-inset-top, 0px))',
            paddingLeft: 'calc(0.75rem + env(safe-area-inset-left, 0px))',
            paddingRight: 'calc(0.75rem + env(safe-area-inset-right, 0px))',
          }}
          data-testid="impersonation-banner"
        >
          <span className="min-w-0 truncate flex-1 sm:flex-initial text-center sm:text-left">
            <span aria-hidden="true">👤</span>{' '}
            <span className="hidden sm:inline">Viewing as </span>
            <strong className="truncate">{user?.full_name || user?.email}</strong>
            <span className="hidden sm:inline"> ({user?.role})</span>
          </span>
          <button
            onClick={stopImpersonating}
            className="flex-shrink-0 px-4 py-2 rounded bg-slate-900 text-white hover:bg-slate-800 active:bg-slate-700 transition text-xs font-bold whitespace-nowrap min-h-[40px]"
            data-testid="stop-impersonating-btn"
          >
            <span className="sm:hidden">STOP</span>
            <span className="hidden sm:inline">STOP — Return to Admin</span>
          </button>
        </div>
      )}
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex flex-col w-64 bg-black text-slate-300 h-screen fixed left-0 top-0 z-40 border-r-0">
        {/* Logo */}
        <div className="px-4 py-4 border-b border-slate-800 flex-shrink-0">
          <Link to="/" className="flex items-center gap-3 px-4">
            <div className="w-10 h-10 bg-amber-500 rounded-md flex items-center justify-center">
              <Briefcase className="w-6 h-6 text-slate-900" />
            </div>
            <span className="text-2xl font-bold text-white font-['Barlow_Condensed'] tracking-wide">FieldReady</span>
          </Link>
        </div>

        {/* Global Search */}
        <div className="px-4 py-3 border-b border-slate-800 flex-shrink-0">
          <GlobalSearch />
        </div>

        {/* Navigation - Scrollable */}
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto min-h-0">
          {allNavItems.map((item) => {
            const isActive = location.pathname === item.path || 
              (item.path !== '/' && location.pathname.startsWith(item.path));
            
            const linkClasses = cn(
              "flex items-center gap-3 px-4 py-2.5 rounded-md transition-all duration-200 font-medium text-sm",
              isActive
                ? "bg-amber-500 text-slate-900 font-bold"
                : "hover:bg-slate-800 hover:text-white"
            );
            
            const linkEl = item.external ? (
              <a
                key={item.path}
                href={item.path}
                target="_blank"
                rel="noopener noreferrer"
                className={linkClasses}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon className="w-4 h-4" />
                <span>{item.label}</span>
              </a>
            ) : (
              <Link
                key={item.path}
                to={item.path}
                className={linkClasses}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon className="w-4 h-4" />
                <span>{item.label}</span>
              </Link>
            );

            if (item.sectionHeader) {
              return (
                <React.Fragment key={item.path}>
                  <p
                    className="px-4 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500"
                    data-testid={`nav-section-${item.sectionHeader.toLowerCase().replace(' ', '-')}`}
                  >
                    {item.sectionHeader}
                  </p>
                  {linkEl}
                </React.Fragment>
              );
            }
            return linkEl;
          })}
        </nav>

        {/* User Section - Fixed at bottom */}
        <div className="p-3 border-t border-slate-800 flex-shrink-0">
          {/* Feedback Link */}
          <Link
            to="/feedback"
            className="flex items-center gap-2 px-3 py-2 mb-2 rounded-md text-slate-300 hover:bg-slate-800 hover:text-white transition-colors text-sm"
            data-testid="nav-feedback"
          >
            <MessageSquarePlus className="w-4 h-4" />
            <span>Feedback</span>
          </Link>
          
          {/* Download User Manual */}
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/export/user-manual-html`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-3 py-2 mb-2 rounded-md text-slate-300 hover:bg-slate-800 hover:text-white transition-colors text-sm"
            data-testid="download-user-manual"
          >
            <BookOpen className="w-4 h-4" />
            <span>User Manual</span>
            <Download className="w-3 h-3 ml-auto text-slate-500" />
          </a>
          
          {/* User Info Card */}
          <div className="flex items-center gap-3 px-3 py-2 mb-1">
            <div 
              className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm shadow-sm"
              style={{ backgroundColor: user?.calendar_color || '#f59e0b', color: getContrastTextColor(user?.calendar_color || '#f59e0b') }}
            >
              {user?.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'U'}
            </div>
            <div className="flex-1 text-left min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.full_name}</p>
              <p className="text-xs text-slate-400 capitalize">{user?.role?.replace('_', ' ')}</p>
            </div>
          </div>
          
          {/* Direct Action Links */}
          <div className="flex gap-1">
            <Link
              to="/profile"
              className="flex items-center gap-2 px-3 py-1.5 rounded-md text-slate-300 hover:bg-slate-800 hover:text-white transition-colors text-sm flex-1"
              data-testid="nav-profile-link"
            >
              <User className="w-4 h-4" />
              <span>Profile</span>
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-3 py-1.5 rounded-md text-red-400 hover:bg-slate-800 hover:text-red-300 transition-colors text-sm"
              data-testid="nav-logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Header — hidden while the menu/search overlay is open so its
          GPU-promoted layer can't visually leak on top of them on iOS (WebKit
          stacking bug with transform-promoted fixed elements). */}
      {!mobileMenuOpen && !mobileSearchOpen && (
      <header
        className="md:hidden fixed top-0 left-0 right-0 bg-white border-b border-slate-200 z-50 flex items-center justify-between px-4"
        style={{
          paddingTop: 'env(safe-area-inset-top)',
          height: 'calc(4rem + env(safe-area-inset-top))',
          // Same GPU-layer trick as the bottom nav — keeps the header pinned to the
          // top during iOS Safari momentum scrolling.
          transform: 'translate3d(0,0,0)',
          WebkitTransform: 'translate3d(0,0,0)',
          WebkitBackfaceVisibility: 'hidden',
          willChange: 'transform',
        }}
      >
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 bg-amber-500 rounded-md flex items-center justify-center">
            <Briefcase className="w-5 h-5 text-slate-900" />
          </div>
          <span className="text-xl font-bold text-slate-900 font-['Barlow_Condensed']">FieldReady</span>
        </Link>
        <div className="flex items-center gap-1">
          <div ref={setMobileBellSlot} />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileSearchOpen(!mobileSearchOpen)}
            data-testid="mobile-search-toggle"
          >
            <Search className="w-5 h-5" />
          </Button>
          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="inline-flex items-center justify-center w-12 h-12 rounded-md text-slate-700 hover:bg-slate-100 active:bg-slate-200 transition"
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
          </button>
        </div>
      </header>
      )}

      {/* Mobile Search Overlay */}
      {mobileSearchOpen && (
        <>
          <div 
            className="md:hidden fixed inset-0 z-[80] bg-black/50 backdrop-blur-sm"
            onClick={() => setMobileSearchOpen(false)}
          />
          <div className="md:hidden fixed top-16 inset-x-4 z-[90] bg-slate-900 rounded-lg shadow-xl p-3">
            <GlobalSearch isMobile={true} />
          </div>
        </>
      )}

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="md:hidden fixed inset-0 z-[60] bg-slate-950"
            onClick={() => setMobileMenuOpen(false)}
          />
          {/* Menu Content */}
          <div className="md:hidden fixed inset-x-0 top-16 bottom-0 z-[70] bg-slate-950 overflow-y-auto overscroll-contain touch-pan-y" data-testid="mobile-menu-content">
            {/* Own close button — the fixed header (with the original hamburger/X
                toggle) is hidden while this menu is open, see Mobile Header above. */}
            <div className="flex items-center justify-end px-4 pt-3 pb-1">
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                className="inline-flex items-center justify-center w-12 h-12 rounded-md text-slate-300 hover:bg-slate-800 active:bg-slate-700 transition"
                aria-label="Close menu"
                data-testid="mobile-menu-close"
              >
                <X className="w-7 h-7" />
              </button>
            </div>
            <nav className="p-4 pt-0 space-y-1 pb-8">
              {allNavItems.map((item) => {
                const isActive = location.pathname === item.path;
                const linkClasses = cn(
                  "flex items-center gap-3 px-4 py-4 rounded-md transition-all duration-200 font-medium text-lg",
                  isActive
                    ? "bg-amber-500 text-slate-900 font-bold"
                    : "text-slate-300 hover:bg-slate-800"
                );
                
                if (item.external) {
                  return (
                    <a
                      key={item.path}
                      href={item.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={linkClasses}
                    >
                      <item.icon className="w-6 h-6" />
                      <span>{item.label}</span>
                    </a>
                  );
                }
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={linkClasses}
                  >
                    <item.icon className="w-6 h-6" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
              <div className="border-t border-slate-700 pt-4 mt-4">
                <Link
                  to="/feedback"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-4 rounded-md text-slate-300 hover:bg-slate-800"
                  data-testid="mobile-nav-feedback"
                >
                  <MessageSquarePlus className="w-6 h-6" />
                  <span>Feedback</span>
                </Link>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/export/user-manual-html`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-4 rounded-md text-slate-300 hover:bg-slate-800"
                  data-testid="mobile-nav-user-manual"
                >
                  <BookOpen className="w-6 h-6" />
                  <span>User Manual</span>
                  <Download className="w-4 h-4 ml-auto text-slate-500" />
                </a>
                <Link
                  to="/profile"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-4 rounded-md text-slate-300 hover:bg-slate-800"
                >
                  <User className="w-6 h-6" />
                  <span>Profile</span>
                </Link>
                <button
                  onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                  className="flex items-center gap-3 px-4 py-4 rounded-md text-red-400 hover:bg-slate-800 w-full"
                >
                  <LogOut className="w-6 h-6" />
                  <span>Sign Out</span>
                </button>
              </div>
            </nav>
          </div>
        </>
      )}

      {/* Mobile Bottom Navigation — hidden while the menu/search overlay is
          open, same reasoning as the header above. */}
      {!mobileMenuOpen && !mobileSearchOpen && (
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 flex items-center justify-around z-50"
        style={{
          paddingBottom: 'env(safe-area-inset-bottom)',
          height: 'calc(4rem + env(safe-area-inset-bottom))',
          // Force into its own GPU compositing layer — fixes iOS Safari momentum-scroll
          // detach where the nav briefly floats into the middle of the screen.
          transform: 'translate3d(0,0,0)',
          WebkitTransform: 'translate3d(0,0,0)',
          WebkitBackfaceVisibility: 'hidden',
          willChange: 'transform',
        }}
      >
        {filteredBottomNavItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                // Fixed-height, top-aligned tab box (h-full + justify-start)
                // instead of vertically centering content — centering is what
                // made "Time Tracker" wrapping to 2 lines push its icon up
                // relative to the other 4 single-line tabs. With a fixed
                // height and top alignment, every icon sits at the exact
                // same y-offset regardless of label height.
                "flex h-full flex-col items-center justify-start text-xs gap-1 py-2 px-3",
                isActive ? "text-amber-600" : "text-slate-500"
              )}
              data-testid={`bottom-nav-${item.viewKey}`}
            >
              <item.icon className="w-5 h-5" />
              {/* whitespace-nowrap guarantees no wrapping; the slightly
                  smaller size below 430px (font-size only, no letter
                  spacing change) is what actually keeps "Time Tracker" on
                  one line at 375-390px — it already fit at 430px+ before. */}
              <span className="font-medium whitespace-nowrap max-[429px]:text-[10px]">{item.label}</span>
            </Link>
          );
        })}
      </nav>
      )}

      {/* Main Content */}
      <main className="md:ml-64 md:pt-0 min-h-screen bg-white" style={{ paddingTop: 'calc(4rem + env(safe-area-inset-top))', paddingBottom: 'calc(5rem + env(safe-area-inset-bottom))' }}>
        {/* Floating notification bell — desktop only (mobile has its own in
            the header). Wrapped in a white "puck" with shadow so it stays
            visually above content instead of tangling with buttons or
            counters that happen to hit the same top-right corner. Page
            content keeps its full width — the bell floats above as content
            scrolls behind it. */}
        <div className="hidden md:block fixed top-4 right-6 z-40 pointer-events-none">
          <div className="bg-white rounded-full shadow-md ring-1 ring-slate-200 p-0.5 pointer-events-auto">
            <div ref={setDesktopBellSlot} />
          </div>
        </div>
        <div className="p-4 md:p-8">
          {children}
        </div>
      </main>
      <NotificationBell mobileSlot={mobileBellSlot} desktopSlot={desktopBellSlot} />
      <PWAInstallPrompt />
    </div>
  );
}
